import { useTranslation } from 'react-i18next'
import { Alpha3Code } from 'i18n-iso-countries'
import { clsx } from '@mantine/styles'
import { useEffect, useRef, useState } from 'react'

import { SearchResultCardLayout } from '../SearchResultCardLayout/SearchResultCardLayout'
import { useAnalytics } from '../../hooks/useAnalytics/useAnalytics'
import { useOrganisationBadge } from '../../hooks/useOrganisationBadge/useOrganisationBadge'

import { Button, Icon, Text, Tooltip } from '@percent/lemonade'
import classes from './OrganisationCard.module.scss'
import fallbackLogo from '@percent/workplace-giving/assets/images/fallback-logo.svg'
import { VerifiedOrganisationSearchResult } from '@percent/workplace-giving/api/search/searchOrganisations/searchVerifiedOrganisations.types'
import { TooltipTaxInformation } from '@percent/workplace-giving/common/components/TooltipTaxInformation/TooltipTaxInformation'
import { OrganisationCardActions } from '@percent/workplace-giving/common/components/OrganisationCardActions/OrganisationCardActions'
import { getDonationOptionsForOrg } from '@percent/workplace-giving/utils/getDonationOptionsForOrg/getDonationOptionsForOrg'
import { scope } from '@percent/workplace-giving/common/hoc/withPermissions'
import { usePermission } from '@percent/workplace-giving/common/hooks/usePermission/usePermission'
import { createShortLink, isIndia, isUsaCalifornia } from '@percent/utility'
import { checkIfTextOverflows } from '@percent/workplace-giving/utils/checkIfTextOverflows'

function WebsiteLink({ url, isOverflowing }: Readonly<{ url: string; isOverflowing: boolean }>) {
  const shortLink = createShortLink(url)
  const link = (
    <a className={classes.Website} href={url} target="_blank" rel="noopener noreferrer">
      {shortLink}
    </a>
  )

  if (isOverflowing) {
    return (
      <Tooltip content={url} placement="top">
        <div className={classes.OverflowingWebsiteWrapper}>{link}</div>
      </Tooltip>
    )
  }

  return link
}

export type OrganisationCardProps = Readonly<{
  organisation: VerifiedOrganisationSearchResult
  handleDonate: (id: string) => Promise<void>
  trackingOrigin: string
  userOrigin: { country: string | null; state?: string | null }
  blockedCountryCodes?: string[]
  extraTrackingProperties?: { messagesExchanged: number }
}>

export function OrganisationCard({
  organisation,
  handleDonate,
  trackingOrigin,
  userOrigin,
  blockedCountryCodes,
  extraTrackingProperties
}: OrganisationCardProps): JSX.Element {
  const { t } = useTranslation()
  const { track } = useAnalytics()
  const has = usePermission()
  const donationsEnabled = has(scope('donation_link'))
  const { badge, badgeTooltip } = useOrganisationBadge(organisation.organisationId)

  const [isCardFocused, setIsCardFocused] = useState(false)
  const [isLinkOverflowing, setIsLinkOverflowing] = useState(false)

  const containerRef = useRef<HTMLDivElement | null>(null)

  const isCaliforniaUser = isUsaCalifornia(userOrigin.country, userOrigin.state)
  const isIndianUser = isIndia(userOrigin.country)

  useEffect(() => {
    if (containerRef.current && organisation?.website) {
      setIsLinkOverflowing(
        checkIfTextOverflows(containerRef.current.clientWidth, createShortLink(organisation.website), 14)
      )
    }
  }, [organisation?.website])

  const donationOptions = getDonationOptionsForOrg({
    orgId: organisation.organisationId,
    orgCountryCode: organisation.countryCode,
    ab488Compliant: !!organisation.ab488Compliant,
    donationsEnabled,
    userOrigin,
    t,
    blockedCountryCodes,
    allowlistScopes: organisation.allowlistScopes
  })

  const handleDonateClicked = () => {
    track({
      event: 'Donation Organisation Donation Start',
      properties: {
        origin: trackingOrigin,
        organisationCountryCode: organisation.countryCode,
        ...extraTrackingProperties
      }
    })
    handleDonate(organisation.organisationId)
  }

  const handleMenuStateChange = (isOpen: boolean) => {
    setIsCardFocused(isOpen)
  }

  return (
    <SearchResultCardLayout
      title={organisation.displayName?.length ? organisation.displayName : organisation.name}
      logo={organisation.logoUrl || fallbackLogo}
      logoBadge={badge}
      logoBadgeTooltip={badgeTooltip}
      additionalClassName={clsx(classes.LayoutCard, { [classes.LayoutCardFocused]: isCardFocused })}
    >
      <div className={classes.Details} ref={containerRef}>
        {organisation?.organisationIdInRegistry ? (
          <Text as="p" size="small">
            {t('workplace_giving.common.registryId', {
              registryId: organisation?.organisationIdInRegistry,
              interpolation: { escapeValue: false }
            })}
          </Text>
        ) : null}
        {organisation?.website ? <WebsiteLink url={organisation.website} isOverflowing={isLinkOverflowing} /> : null}
      </div>
      <div className={classes.ActionsWrapper}>
        {donationOptions.kind === 'allowed' && (
          <div className={classes.DonateBtnWrapper}>
            <Button shape="oval" size="small" onClick={handleDonateClicked}>
              {t('workplace_giving.common.donate')}
            </Button>
          </div>
        )}
        <div
          className={clsx(classes.ActionsMenuWrapper, {
            [classes.ActionsMenuWrapperAnimated]: donationOptions.kind === 'allowed'
          })}
        >
          <OrganisationCardActions
            organisation={{
              ...organisation,
              id: organisation.organisationId,
              logoUrl: organisation.logoUrl || null,
              countryCode: organisation.countryCode as Alpha3Code
            }}
            ab488Compliant={organisation.ab488Compliant}
            onChange={handleMenuStateChange}
            donationsNotAllowed={donationOptions.kind === 'disallowed'}
            allowlistScopes={organisation.allowlistScopes}
          />
        </div>

        <div
          className={clsx(classes.TooltipWrapper, {
            [classes.TooltipWrapperAnimated]: donationOptions.kind === 'allowed'
          })}
        >
          {(isCaliforniaUser && organisation.ab488Compliant === false) || isIndianUser ? null : (
            <TooltipTaxInformation
              orgCountryCode={organisation.countryCode as Alpha3Code}
              placement="top"
              showIconOnly
            />
          )}

          {donationOptions.kind === 'disallowed' ? (
            <Tooltip content={donationOptions.reason} placement="top">
              <div data-testid="tooltip-disallowed-donation-info">
                <Icon name="info" size="m" color="neutral300" />
              </div>
            </Tooltip>
          ) : null}
        </div>
      </div>
    </SearchResultCardLayout>
  )
}
